import { generateBooleanSchema, generateColorSchema, generateDateTimeSchema, generateRootObjectSchema, generateStringSchema, generateArraySchema } from "components/cards/common/schema";
import { BaseAtomSpec, generateAtomContainerSchema } from "components/cards/atoms/BaseAtomSpec";
import { TextParticleSpec, ImageParticleSpec, ButtonParticleSpec } from "components/cards/particles/YinzCam";

export const specification = new BaseAtomSpec(
  'Schedule/Fixtures Stacked B',
  generateRootObjectSchema({
      container: generateAtomContainerSchema({
        defaultValues: { background: { visible: true } },
      }),

      // Card-level styling
      colorCardBackground: generateColorSchema('Card Background', 'Background color for the card'),
      cardPadding: generateStringSchema('Card Padding', 'Inner padding of the card', {
        defaultValue: '1.5rem',
      }),

      // Match data
      matchDateTime: generateDateTimeSchema('Match Date Time', 'The date and time of the match.'),
      matchStatus: generateStringSchema('Match Status', 'Select match status', {
        choices: ['pre', 'live', 'post'],
        choiceTitles: ['Pre', 'Live', 'Post'],
      }),

      // Top row: date/time (shared style)
      txtDateTime: TextParticleSpec.generateSchema(
        'Date & Time Text Style',
        'Shared styling for date and time text in the top row.',
        {
          defaultValues: { size: '0.9375rem', weight: '500', color: '#f0f0f0' },
          uneditableFields: { text: true },
        }
      ),

      // Team tag badge
      teamTagText: generateStringSchema('Team Tag', 'Team variant tag text (e.g. MEN, WOMEN, U18, U20)', {
        defaultValue: '',
      }),
      txtTeamTag: TextParticleSpec.generateSchema('Team Tag Style', 'Styling for the team tag badge text.', {
        defaultValues: {
          size: '0.6875rem',
          weight: '600',
          color: '#0f0f0f',
          transform: 'uppercase',
        },
        uneditableFields: { text: true },
      }),
      colorTeamTagBackground: generateColorSchema('Team Tag Background', 'Background color for the team tag badge'),

      // Live indicator
      txtLive: TextParticleSpec.generateSchema(
        'Live Badge Text',
        'Styling for the LIVE text in the live indicator badge.',
        {
          defaultValues: {
            size: '0.625rem',
            weight: '700',
            color: '#ffffff',
            text: 'LIVE',
            transform: 'uppercase',
            lineHeight: '1',
          },
        }
      ),
      colorLiveBackground: generateColorSchema('Live Badge Background', 'Background color for the live indicator pill'),
      colorLiveBorder: generateColorSchema('Live Badge Border', 'Border color for the live indicator pill'),
      colorLiveDot: generateColorSchema('Live Dot Color', 'Color of the animated dot in the live badge'),

      // Team names (SHARED styling)
      txtTeamName: TextParticleSpec.generateSchema(
        'Team Name Style',
        'Shared styling applied to BOTH home and away team names.',
        {
          defaultValues: {
            size: '1.5rem',
            weight: '700',
            color: 'var(--theme-mode-foreground-color, #fff)',
            transform: 'uppercase',
            align: 'left',
            container: { padding: '0 1rem' },
          },
          uneditableFields: { text: true },
        }
      ),
      txtHomeTeam: TextParticleSpec.generateSchema(
        'Home Team Name',
        'Home team display name (text populated by data source).',
        {
          defaultValues: {
            container: { centerContentHorizontally: false, centerContentVertically: false },
          },
          uneditableFields: { text: true },
        }
      ),
      txtAwayTeam: TextParticleSpec.generateSchema(
        'Away Team Name',
        'Away team display name (text populated by data source).',
        {
          defaultValues: {
            container: { centerContentHorizontally: false, centerContentVertically: false },
          },
          uneditableFields: { text: true },
        }
      ),

      txtHomeTeamAbbr: TextParticleSpec.generateSchema(
        'Home Team Tri-code',
        'Home Team Tri-code(text populated by data source).',
        {
          defaultValues: {
            size: '1.5rem',
            weight: '700',
            transform: 'uppercase',
            align: 'left',
            container: { centerContentHorizontally: false, centerContentVertically: false },
          },
          uneditableFields: { text: true },
        }
      ),
      txtAwayTeamAbbr: TextParticleSpec.generateSchema(
        'Away Team Tri-code',
        'Away Team Tri-code (text populated by data source).',
        {
          defaultValues: {
            size: '1.5rem',
            weight: '700',
            transform: 'uppercase',
            align: 'left',
            container: { centerContentHorizontally: false, centerContentVertically: false },
          },
          uneditableFields: { text: true },
        }
      ),
      // Team logos (shared sizing, separate sources)
      imgTeamLogo: ImageParticleSpec.generateSchema('Team Logo Style', 'Shared sizing/styling for both team logos.', {
        defaultValues: {
          container: { height: '3.5rem', width: '3.5rem' },
          sizing: 'contain',
        },
      }),
      imgHomeTeam: ImageParticleSpec.generateSchema(
        'Home Team Logo',
        'Home team logo (source populated by data source).',
        {
          defaultValues: {},
        }
      ),
      imgAwayTeam: ImageParticleSpec.generateSchema(
        'Away Team Logo',
        'Away team logo (source populated by data source).',
        {
          defaultValues: {},
        }
      ),

      // Team scores (shown when match is live or post)
      txtTeamScore: TextParticleSpec.generateSchema(
        'Team Score Style',
        'Shared styling applied to BOTH home and away team scores. Scores are shown when the match is live or post.',
        {
          defaultValues: {
            size: '3.5rem',
            weight: '700',
            align: 'right',
            color: 'var(--theme-mode-foreground-color, #fff)',
          },
          uneditableFields: { text: true },
        }
      ),
      homeTeamScore: TextParticleSpec.generateSchema(
        'Home Team Score',
        'Home team score (text populated by data source).',
        {
          defaultValues: {},
          uneditableFields: { text: true },
        }
      ),
      awayTeamScore: TextParticleSpec.generateSchema(
        'Away Team Score',
        'Away team score (text populated by data source).',
        {
          defaultValues: {},
          uneditableFields: { text: true },
        }
      ),

      // CTA Buttons (shared styling via btnCTAStyle)
      btnCTA: generateArraySchema(
        'CTA Buttons',
        'List of CTA buttons with text and link per button.',
        ButtonParticleSpec.generateSchema('CTA Button', 'A CTA button', {
          defaultValues: {
            container: {
              padding: '0.5rem 1rem',
              border: { visible: true, radius: '1.25rem' },
              background: { visible: true, color: '#ffffff' },
              fillWidth: false,
            },
            text: {
              size: '0.875rem',
              weight: '600',
              color: '#202020',
              align: 'center',
              transform: 'uppercase',
            },
          },
        })
      ),
      btnCTAStyle: ButtonParticleSpec.generateSchema(
        'CTA Button Shared Style',
        'Shared styling applied to ALL CTA buttons. Individual button text/link overrides this.',
        {
          defaultValues: {
            container: {
              padding: '0.5rem 1rem',
              border: { visible: true, radius: '1.25rem' },
              background: { visible: true, color: '#ffffff' },
              fillWidth: false,
            },
            text: {
              size: '0.875rem',
              weight: '600',
              color: '#202020',
              align: 'center',
              transform: 'uppercase',
            },
          },
        }
      ),

      // Layout tuning
      teamRowGap: generateStringSchema('Team Row Gap', 'Gap between home and away team rows', {
        defaultValue: '0.75rem',
      }),
      ctaButtonGap: generateStringSchema('CTA Button Gap', 'Gap between CTA buttons', {
        defaultValue: '0.625rem',
      }),

      // Visibility toggles
      showCTA: generateBooleanSchema('Show CTA Buttons', 'Toggle CTA buttons visibility', {
        defaultValue: true,
      }),
      showLiveIndicator: generateBooleanSchema('Show Live Indicator', 'Toggle the live badge visibility', {
        defaultValue: true,
      }),
      showTeamTag: generateBooleanSchema('Show Team Tag', 'Toggle the team tag (e.g. MEN, WOMEN, U18)', {
        defaultValue: true,
      }),
      showDateTime: generateBooleanSchema('Show Date & Time', 'Toggle date and time visibility', {
        defaultValue: true,
      }),
    })
  );
